import type { IslandPart, Recording, SessionPlan } from '../types.ts';
import { islandParts } from './parts.ts';
import { scheduleOps, ToneStrategyError } from './schedule.ts';
import type { ScheduleOp } from './schedule.ts';
import {
  FRAME_RATE, MAX_ENVELOPE_BYTES, MAX_FADE_FRAMES, MAX_OP_FRAME_COUNT, MAX_OPS,
  PROTOCOL_VERSION, assertNever, assetFrameLimit, secondsToFrame, sourceAssetKind,
} from './native-protocol.ts';
import type { NativeAsset, NativeEnvelope, NativeOp, NativeSource } from './native-protocol.ts';

export interface CompileOptions {
  manifestVersion: string;
  /** The loaded profile's RMS; scheduling has no spectrum from which to measure it. */
  targetRms?: number;
  chunkSec?: number;
}

/**
 * Why a residue could not be absorbed. Four rules refuse for four different reasons, and
 * one message for all of them is a diagnostic that cannot be acted on — it also let a test
 * assert the message and pass while a different rule did the throwing, which is how the
 * `neighbour-is-speech` guard came to be uncovered.
 */
export type ReconciliationReason =
  /** The next op is speech; growing it would make it read past what the schedule says. */
  | 'neighbour-is-speech'
  /** Nothing follows: the session ends on a part that decoded short. This is D-011. */
  | 'no-neighbour'
  /** The part supplies no frames at this offset at all. */
  | 'nothing-available'
  /** The session's last op is speech and does not reach `totalFrameCount`. */
  | 'session-tail';

export class FrameReconciliationError extends Error {
  readonly recordingId: string;
  readonly opIndex: number;
  readonly residueFrames: number;
  readonly reason: ReconciliationReason;

  constructor(recordingId: string, opIndex: number, residueFrames: number,
              reason: ReconciliationReason) {
    super(`${recordingId}: cannot reconcile ${residueFrames} frames at op ${opIndex} ` +
      `(${reason}); no neighbouring tone or silence can absorb the residue without ` +
      'extending speech');
    this.name = 'FrameReconciliationError';
    this.recordingId = recordingId;
    this.opIndex = opIndex;
    this.residueFrames = residueFrames;
    this.reason = reason;
  }
}

// IslandPart predates the decoder measurement. Keep the wire requirement local until
// the manifest's shared model is updated; a cast alone must never admit an old manifest.
type MeasuredPart = IslandPart & { pcmTotalFrames?: number };

function integer(value: number, label: string, min = 0, max = Number.MAX_SAFE_INTEGER): number {
  if (!Number.isSafeInteger(value) || value < min || value > max) {
    throw new Error(`${label} must be an exact integer in [${min}, ${max}], got ${value}`);
  }
  return value;
}

function boundaries(op: ScheduleOp): [number, number, number, number] {
  return [op.atSec, op.atSec + op.fadeInSec,
    op.atSec + op.durSec - op.fadeOutSec, op.atSec + op.durSec];
}

function canonicalFrames(schedule: ScheduleOp[], actualSec: number): Map<number, number> {
  const times = [...new Set([0, actualSec, ...schedule.flatMap(boundaries)])].sort((a, b) => a - b);
  const frames = new Map<number, number>();
  for (let i = 0; i < times.length;) {
    const first = i++;
    if (!Number.isFinite(times[first])) throw new Error('schedule boundary must be finite');
    // Compare with the cluster's first time: chaining near neighbours could merge
    // instants more than the scheduler's own sameTime tolerance apart.
    while (i < times.length && times[i] - times[first] <= 1e-9) i++;
    const cluster = times.slice(first, i);
    // The session endpoints own their clusters so the wire's total remains exactly
    // secondsToFrame(actualSec), even when a neighbouring sum lies one ulp below it.
    const time = cluster.includes(0) ? 0 : cluster.includes(actualSec) ? actualSec : times[first];
    const frame = integer(secondsToFrame(time), 'boundary frame');
    for (const member of cluster) frames.set(member, frame);
  }
  return frames;
}

export function compileEnvelope(
  rec: Recording, plan: SessionPlan, opts: CompileOptions,
): NativeEnvelope {
  const parts = rec.speech.map((_, island) => islandParts(rec, island) as MeasuredPart[]);
  const pcmFrames = (island: number, part: number): number => {
    const value = parts[island]?.[part]?.pcmTotalFrames;
    if (value === undefined) {
      throw new Error(`${rec.id}#${island}.${part}: missing pcmTotalFrames; regenerate the manifest`);
    }
    return integer(value, `${rec.id}#${island}.${part} pcmTotalFrames`, 1);
  };
  parts.forEach((island, i) => island.forEach((_, p) => pcmFrames(i, p)));

  const schedule = scheduleOps(rec, plan, {
    chunkSec: opts.chunkSec,
    container: 'opus',
    bufferDurationSec: (island, part) => pcmFrames(island, part) / FRAME_RATE,
  });
  // scheduleOps throws itself when there are gaps. A speech-only plan still needs a
  // representable strategy on the wire, so the same error also covers that case.
  const toneStrategy = rec.toneProfile ? 'synth' : rec.tone === 'inaudible' ? 'inaudible' : null;
  if (toneStrategy === null) throw new ToneStrategyError(rec.id, rec.tone);
  if (typeof opts.manifestVersion !== 'string' || !opts.manifestVersion) {
    throw new Error('manifestVersion must be a non-empty cache identity');
  }
  const totalFrameCount = integer(secondsToFrame(plan.actualSec), 'totalFrameCount', 1);
  if (schedule.length === 0 || schedule.length > MAX_OPS) {
    throw new Error(`schedule must contain 1..${MAX_OPS} ops, got ${schedule.length}`);
  }
  const frames = canonicalFrames(schedule, plan.actualSec);
  const assets = new Map<string, NativeAsset>();
  const sourceFor = (op: ScheduleOp): NativeSource => {
    const prov = op.prov;
    switch (prov.kind) {
      case 'native': {
        const part = parts[prov.island][prov.part];
        const file = part.files?.opus;
        if (!file?.path) throw new Error(`${rec.id}#${prov.island}.${prov.part}: missing Opus asset`);
        const assetId = `part:${prov.island}:${prov.part}`;
        assets.set(assetId, { assetId, kind: 'part', path: file.path,
          byteLength: integer(file.bytes, `${assetId} byteLength`, 1),
          pcmTotalFrames: pcmFrames(prov.island, prov.part) });
        return { kind: 'speech', assetId,
          offsetFrame: integer(secondsToFrame(op.offsetSec), `${assetId} offsetFrame`) };
      }
      case 'synth': {
        if (toneStrategy !== 'synth' || !rec.toneProfile) throw new Error('tone requires synth strategy');
        const targetRms = prov.targetRms ?? opts.targetRms;
        if (targetRms === undefined || !Number.isFinite(targetRms) || targetRms < 0) {
          throw new Error('synth tone requires a finite, non-negative targetRms');
        }
        const assetId = 'profile:0';
        assets.set(assetId, { assetId, kind: 'profile', path: rec.toneProfile, byteLength: 4098 });
        return { kind: 'tone', assetId, seed: integer(prov.seed, 'tone seed'), targetRms };
      }
      case 'silence':
        if (toneStrategy !== 'inaudible') throw new Error('silence requires inaudible strategy');
        return { kind: 'silence' };
      default: return assertNever(prov);
    }
  };

  const ops: NativeOp[] = schedule.map((op) => {
    const [start, head, tail, end] = boundaries(op).map((time) => frames.get(time)!);
    const stop = Math.min(end, totalFrameCount);
    return {
      atFrame: start,
      frameCount: stop - start,
      fadeInFrames: Math.min(head, stop) - start,
      fadeOutFrames: stop - Math.min(tail, stop),
      source: sourceFor(op),
      prov: op.prov,
      reconciledFrames: 0,
    };
  });

  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    if (op.source.kind !== 'speech') continue;
    const limit = assetFrameLimit(assets.get(op.source.assetId)!)!;
    const available = limit - op.source.offsetFrame;
    const residue = op.frameCount - available;
    if (residue <= 0) continue;
    const after = ops[i + 1];
    if (!after || after.source.kind === 'speech' || available <= 0) {
      throw new FrameReconciliationError(rec.id, i, residue,
        available <= 0 ? 'nothing-available'
          : !after ? 'no-neighbour' : 'neighbour-is-speech');
    }
    // Translate the already-declared shared window by the clamp, preserving its
    // width. Only generated material grows; no later speech or chunk is rescheduled.
    op.frameCount = available;
    after.atFrame -= residue;
    after.frameCount += residue;
    after.reconciledFrames += residue;
  }

  const last = ops[ops.length - 1];
  const tailResidue = totalFrameCount - (last.atFrame + last.frameCount);
  if (tailResidue > 0) {
    if (last.source.kind === 'speech') {
      throw new FrameReconciliationError(rec.id, ops.length - 1, tailResidue, 'session-tail');
    }
    last.frameCount += tailResidue;
    last.reconciledFrames += tailResidue;
  }

  // Fail at the compilation seam rather than emit a schedule the native side cannot
  // represent. These are construction checks, not a second schedule or a repair pass.
  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    integer(op.atFrame, `op ${i} atFrame`);
    integer(op.frameCount, `op ${i} frameCount`, 1, MAX_OP_FRAME_COUNT);
    integer(op.atFrame + op.frameCount, `op ${i} endFrame`);
    integer(op.fadeInFrames, `op ${i} fadeInFrames`, 0, MAX_FADE_FRAMES);
    integer(op.fadeOutFrames, `op ${i} fadeOutFrames`, 0, MAX_FADE_FRAMES);
    integer(op.reconciledFrames, `op ${i} reconciledFrames`, -Number.MAX_SAFE_INTEGER);
    if (op.fadeInFrames + op.fadeOutFrames > op.frameCount) {
      throw new Error(`op ${i}: fades exceed frameCount after conversion`);
    }
    const kind = sourceAssetKind(op.source);
    if ('assetId' in op.source && assets.get(op.source.assetId)?.kind !== kind) {
      throw new Error(`op ${i}: missing or wrong-kind asset`);
    }
    const before = ops[i - 1];
    if (!before) {
      if (op.atFrame !== 0) throw new Error('schedule does not cover frame 0');
      continue;
    }
    if (op.atFrame < before.atFrame) throw new Error(`op ${i}: clamp reverses schedule order`);
    const overlap = before.atFrame + before.frameCount - op.atFrame;
    if (overlap < 0) throw new Error(`op ${i}: uncovered ${-overlap} frames before op`);
    if (overlap > 0 && (overlap !== before.fadeOutFrames || overlap !== op.fadeInFrames)) {
      throw new Error(`op ${i}: overlap does not equal both declared fades`);
    }
    const earlier = ops[i - 2];
    if (earlier && earlier.atFrame + earlier.frameCount > op.atFrame) {
      throw new Error(`op ${i}: conversion creates triple coverage`);
    }
  }
  if (assets.size === 0) throw new Error('native envelope requires at least one asset');
  const envelope: NativeEnvelope = {
    protocolVersion: PROTOCOL_VERSION, sampleRate: FRAME_RATE,
    recordingId: rec.id, manifestVersion: opts.manifestVersion, toneStrategy,
    totalFrameCount, assets: [...assets.values()], ops,
  };
  // JSON.stringify escapes lone surrogates; counting code points here matches UTF-8
  // on disk without pulling a browser or Node I/O dependency into the compiler.
  let bytes = 0;
  for (const char of JSON.stringify(envelope)) {
    const cp = char.codePointAt(0)!;
    bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
  }
  if (bytes > MAX_ENVELOPE_BYTES) throw new Error('serialized native envelope exceeds 4 MiB');
  return envelope;
}
